import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames/bind';
import get from 'lodash/get';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import ChevronRight from 'yoda-core-components/lib/assets/svg/ChevronRight.svg';
import ChevronLeft from 'yoda-core-components/lib/assets/svg/ChevronLeft.svg';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import CashpassCard from '../../CashpassCard/CashpassCard';
import * as styles from './AvailableRewards.css';

const cx = classNames.bind(styles);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Formats a certificate expiry date (e.g. "10/31/2026") into the display
 * format "Sep 13,2026" (abbreviated month, day, comma, year).
 * Returns the original value unchanged when it cannot be parsed.
 */
export const formatExpiryDate = (expiryDate) => {
    if (!expiryDate) return '';
    const parts = String(expiryDate).split('/');
    if (parts.length !== 3) return expiryDate;
    const [month, day, year] = parts;
    const monthIndex = parseInt(month, 10) - 1;
    if (monthIndex < 0 || monthIndex > 11) return expiryDate;
    return `${MONTHS[monthIndex]} ${parseInt(day, 10)},${year}`;
};

/**
 * Parses a certificate expiry date ("MM/DD/YYYY") into a sortable timestamp.
 * Returns Number.MAX_SAFE_INTEGER for missing/invalid dates so those
 * certificates sort to the end.
 * @param {string} expiryDate
 * @returns {number}
 */
export const parseExpiryTimestamp = (expiryDate) => {
    if (!expiryDate) return Number.MAX_SAFE_INTEGER;
    const parts = String(expiryDate).split('/');
    if (parts.length !== 3) return Number.MAX_SAFE_INTEGER;
    const [month, day, year] = parts.map((p) => parseInt(p, 10));
    const timestamp = new Date(year, month - 1, day).getTime();
    return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
};

/**
 * Returns a new array of certificates ordered by the earliest expiry date
 * first. Does not mutate the input; invalid/missing dates go last.
 * @param {Array<Object>} certificates
 * @returns {Array<Object>}
 */
export const sortByEarliestExpiry = (certificates) =>
    [...certificates].sort(
        (a, b) => parseExpiryTimestamp(a?.expiryDate) - parseExpiryTimestamp(b?.expiryDate)
    );

/**
 * Default icon key used by CashpassCard when a certificate's rewardCode does
 * not match any configured prefix (welcome, VIP, reward, unknown all use this).
 */
export const DEFAULT_CASHPASS_ICON = 'JCPRWD';

/**
 * Maps a certificate `rewardCode` prefix to the icon key consumed by
 * CashpassCard. Matching is done by prefix so promo/year/amount variants of a
 * code (e.g. BIRTHDAY, BIRTHDAY10, CHALLENGE5CASHPASS) resolve correctly.
 *
 * To add a new icon for a new rewardCode:
 *   1. Register the SVG under an icon key in CashpassCard's CASHPASS_ICONS map.
 *   2. Add an entry here: { prefix: '<REWARD_CODE_PREFIX>', icon: '<iconKey>' }.
 * Order matters: the first matching prefix wins.
 */
const CASHPASS_ICON_PREFIXES = [
    { prefix: 'BIRTHDAY', icon: 'birthday' },
    { prefix: 'CHALLENGE', icon: 'challenge' },
];

/**
 * Resolves the CashpassCard icon key from a certificate rewardCode using the
 * configured prefix map, falling back to the reward icon.
 * @param {string} rewardCode - the certificate rewardCode from the API.
 * @returns {string} icon key understood by CashpassCard.
 */
export const resolveCashpassIcon = (rewardCode) => {
    const normalizedCode = String(rewardCode || '').toUpperCase();
    const match = CASHPASS_ICON_PREFIXES.find((entry) => normalizedCode.startsWith(entry.prefix));
    return match ? match.icon : DEFAULT_CASHPASS_ICON;
};

/**
 * Static display-copy overrides keyed by the API `name`. Only the names listed
 * here are remapped; every other certificate keeps its API `name` verbatim.
 */
const CASHPASS_TITLE_OVERRIDES = {
    'Challenge $5 Cashpass': '$5 Challenge Cashpass',
    'Birthday $10 Cashpass': '$10 Birthday Cashpass',
};

/**
 * Resolves the display title for a certificate.
 * - "Reward Cashpass" is shown as "$<certValue> Cashpass", with the dollar
 *   amount pulled from the certificate's `certValue` attribute.
 * - Names in CASHPASS_TITLE_OVERRIDES use their mapped copy.
 * - Everything else keeps the API `name` unchanged.
 * @param {Object} certificate - a single entry from certificates.data.
 * @returns {string} the customer-facing title.
 */
export const resolveCashpassTitle = (certificate) => {
    const name = get(certificate, 'name');
    if (name === 'Reward Cashpass') {
        const certValue = get(certificate, 'certValue');
        // Fall back to the raw name if the amount is missing so we never show "$ Cashpass".
        return certValue || certValue === 0 ? `$${certValue} Cashpass` : name;
    }
    return CASHPASS_TITLE_OVERRIDES[name] || name;
};

/**
 * Builds the props CashpassCard needs from a raw certificate API object.
 * The title is the API `name`, remapped for the few certificates whose display
 * copy differs (see resolveCashpassTitle); the icon is resolved from the
 * rewardCode prefix (defaulting to reward), and the expiry date is formatted
 * for display.
 * @param {Object} certificate - a single entry from certificates.data.
 * @returns {{ cashpassType: string, title: string, eligible: string, expire: string, automationId: string }}
 */
export const buildCashpassCardProps = (certificate) => ({
    cashpassType: resolveCashpassIcon(get(certificate, 'rewardCode')),
    title: resolveCashpassTitle(certificate),
    eligible: get(certificate, 'eligible'),
    expire: formatExpiryDate(get(certificate, 'expiryDate')),
    automationId: `cashpass-card-${get(certificate, 'serialNumber')}`,
});

/**
 * Renders the available reward certificates as a horizontally scrollable
 * carousel of CashpassCard components. Left/right navigation arrows appear
 * only when the cards overflow the visible track width, and each arrow is
 * disabled once the track has been scrolled to its corresponding edge.
 */
class AvailableRewards extends Component {
    static defaultProps = {
        certificates: [],
        appliedMap: {},
        onApply: () => {},
        onRemove: () => {},
        enableExclusionsSlidePanel: true,
    };

    static propTypes = {
        /** Array of reward certificates from rewardsDetails.data.certificates.data. */
        certificates: PropTypes.arrayOf(PropTypes.object),
        /** Map of serialNumber -> { id, applied } for cashpasses applied to the cart. */
        appliedMap: PropTypes.objectOf(PropTypes.object),
        /** Called with the certificate when the user applies a cashpass to the cart. */
        onApply: PropTypes.func,
        /** Called with { id, serialNumber } when the user removes a cashpass from the cart. */
        onRemove: PropTypes.func,
        /**
         * Feature flag forwarded to each CashpassCard. When true (default),
         * "Exclusions & Details" opens in a slide panel; when false it opens
         * the standalone page in a new tab.
         */
        enableExclusionsSlidePanel: PropTypes.bool,
    };

    constructor(props) {
        super(props);
        this.state = {
            canScrollLeft: false,
            canScrollRight: false,
        };
        this.trackRef = React.createRef();
    }

    componentDidMount() {
        this.updateScrollState();
        /* istanbul ignore else */
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.updateScrollState);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.certificates !== this.props.certificates) {
            this.updateScrollState();
        }
    }

    componentWillUnmount() {
        /* istanbul ignore else */
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.updateScrollState);
        }
    }

    /**
     * Amount to scroll per arrow click. Scrolls by roughly one viewport width
     * so the user advances through a full "page" of visible cards.
     */
    getScrollAmount = () => {
        const track = this.trackRef.current;
        /* istanbul ignore if */
        if (!track) return 0;
        // Fall back to a single card width (280 + 16 gap) when clientWidth is 0 (jsdom).
        return track.clientWidth || 296;
    };

    /**
     * Recalculate whether the left/right arrows should be shown based on the
     * current scroll position and the overflow of the track.
     */
    updateScrollState = () => {
        const track = this.trackRef.current;
        /* istanbul ignore if */
        if (!track) return;
        const { scrollLeft, scrollWidth, clientWidth } = track;
        const maxScrollLeft = scrollWidth - clientWidth;
        this.setState({
            canScrollLeft: scrollLeft > 1,
            canScrollRight: scrollLeft < maxScrollLeft - 1,
        });
    };

    handleScrollLeft = () => {
        const track = this.trackRef.current;
        /* istanbul ignore if */
        if (!track) return;
        const amount = this.getScrollAmount();
        if (typeof track.scrollBy === 'function') {
            track.scrollBy({ left: -amount, behavior: 'smooth' });
        } else {
            track.scrollLeft -= amount;
        }
        // Update arrows after the scroll settles.
        setTimeout(this.updateScrollState, 350);
    };

    handleScrollRight = () => {
        const track = this.trackRef.current;
        /* istanbul ignore if */
        if (!track) return;
        const amount = this.getScrollAmount();
        if (typeof track.scrollBy === 'function') {
            track.scrollBy({ left: amount, behavior: 'smooth' });
        } else {
            track.scrollLeft += amount;
        }
        setTimeout(this.updateScrollState, 350);
    };

    render() {
        const {
            certificates,
            appliedMap,
            onApply,
            onRemove,
            enableExclusionsSlidePanel,
        } = this.props;
        const { canScrollLeft, canScrollRight } = this.state;

        // Empty state is owned by the parent (RewardsHistory.renderAvailableRewards),
        // so render nothing here when there are no certificates.
        if (!Array.isArray(certificates) || certificates.length === 0) {
            return null;
        }

        // Display cards ordered by the earliest-expiring certificate first.
        const sortedCertificates = sortByEarliestExpiry(certificates);

        return (
            <div
                className={`${cx('availableRewardsWrapper')} ${dt([
                    'float-left',
                    'w-full',
                    'relative',
                    'smOnly:mt-4',
                    'mt-4',
                ])}`}
                data-automation-id="available_rewards"
            >
                {canScrollLeft && (
                    <button
                        type="button"
                        onClick={this.handleScrollLeft}
                        aria-label="Scroll to previous rewards"
                        data-automation-id="available_rewards_prev"
                        className={`${cx('carouselArrow', 'carouselArrowLeft')} ${dt([
                            'absolute',
                            'left-0',
                            'top-1/2',
                            '-translate-y-1/2',
                            'z-10',
                            'flex',
                            'items-center',
                            'justify-center',
                            'w-10',
                            'h-10',
                            'rounded-full',
                            'bg-white',
                            'border',
                            'border-solid',
                            'border-gray-25',
                            'cursor-pointer',
                            'focus:outline',
                            'focus:outline-2',
                            'focus:outline-brand-red',
                        ])}`}
                    >
                        <Icon
                            iconType="svg"
                            width="24"
                            height="24"
                            viewBox="0 0 40 40"
                            automationId="available-rewards-chevron-left"
                        >
                            <ChevronLeft />
                        </Icon>
                    </button>
                )}

                <div
                    ref={this.trackRef}
                    onScroll={this.updateScrollState}
                    className={`${cx('carouselTrack')} ${dt([
                        'flex',
                        'flex-row',
                        'flex-nowrap',
                        'gap-4',
                        'overflow-x-auto',
                        'scroll-smooth',
                        'w-full',
                        'py-2',
                    ])}`}
                    data-automation-id="available_rewards_track"
                >
                    {sortedCertificates.map((certificate) => {
                        const cardProps = buildCashpassCardProps(certificate);
                        const serialNumber = get(certificate, 'serialNumber');
                        const appliedEntry = appliedMap[serialNumber] || {};
                        const isAppliedToCart = Boolean(appliedEntry.applied);
                        return (
                            <div
                                key={get(certificate, 'id') || serialNumber}
                                className={dt(['shrink-0'])}
                            >
                                <CashpassCard
                                    cashpassType={cardProps.cashpassType}
                                    title={cardProps.title}
                                    eligible={cardProps.eligible}
                                    expire={cardProps.expire}
                                    automationId={cardProps.automationId}
                                    isAppliedToCart={isAppliedToCart}
                                    onApplyToCart={() => onApply(certificate)}
                                    onRemoveFromCart={() =>
                                        onRemove({ id: appliedEntry.id, serialNumber })
                                    }
                                    enableExclusionsSlidePanel={enableExclusionsSlidePanel}
                                />
                            </div>
                        );
                    })}
                </div>

                {canScrollRight && (
                    <button
                        type="button"
                        onClick={this.handleScrollRight}
                        aria-label="Scroll to next rewards"
                        data-automation-id="available_rewards_next"
                        className={`${cx('carouselArrow', 'carouselArrowRight')} ${dt([
                            'absolute',
                            'right-0',
                            'top-1/2',
                            '-translate-y-1/2',
                            'z-10',
                            'flex',
                            'items-center',
                            'justify-center',
                            'w-10',
                            'h-10',
                            'rounded-full',
                            'bg-white',
                            'border',
                            'border-solid',
                            'border-gray-25',
                            'cursor-pointer',
                            'focus:outline',
                            'focus:outline-2',
                            'focus:outline-brand-red',
                        ])}`}
                    >
                        <Icon
                            iconType="svg"
                            width="24"
                            height="24"
                            viewBox="0 0 40 40"
                            automationId="available-rewards-chevron-right"
                        >
                            <ChevronRight />
                        </Icon>
                    </button>
                )}
            </div>
        );
    }
}

export default AvailableRewards;
